import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box, Card, Typography, TextField, Button, Divider,
  Autocomplete, CircularProgress
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from '../Spinner';
import { FunctionalityDTO, ScopeFunctionalityDTO } from 'axiosApi/models';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { useDialog } from 'common/DialogProvider';
import intl from 'components/i18n/ReactIntlWrapper';

const ScopeDetail = () => {
  const api: Api = useApi();
  const navigate = useNavigate();
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();
  const { id } = useParams();
  const idNumber = id ? parseInt(id) : null;
  const isNew = !idNumber;

  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disabled, setDisabled] = useState(!isNew);

  const [assignments, setAssignments] = useState<ScopeFunctionalityDTO[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  const [funcOptions, setFuncOptions] = useState<FunctionalityDTO[]>([]);
  const [funcSearchOpen, setFuncSearchOpen] = useState(false);
  const [funcSearchLoading, setFuncSearchLoading] = useState(false);
  const [funcSearchTerm, setFuncSearchTerm] = useState('');
  const [selectedFunc, setSelectedFunc] = useState<FunctionalityDTO | null>(null);

  const fetchScope = async () => {
    if (!idNumber) return;
    setLoading(true);
    try {
      const res = await api.employeeApi.apiVversionScopeIdGet(idNumber, '1', {});
      setDescription(res.data?.data?.scope || '');
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    if (!idNumber) return;
    setAssignmentsLoading(true);
    try {
      const res = await api.employeeApi.apiVversionFunctionalityScopeIdByScopeGet(idNumber, '1', {});
      setAssignments(res.data?.data?.queryResult || []);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setAssignmentsLoading(false);
    }
  };

  useEffect(() => {
    fetchScope();
    fetchAssignments();
  }, [idNumber]);

  useEffect(() => {
    if (!funcSearchOpen) return;
    setFuncSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.employeeApi.apiVversionFunctionalityAllGet('1', 1, 20, undefined, funcSearchTerm || undefined, {});
        setFuncOptions(res.data?.data?.queryResult || []);
      } catch (e) {
        handleAPIError(e, toast, {});
      } finally {
        setFuncSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [funcSearchTerm, funcSearchOpen]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.employeeApi.apiVversionScopePost('1', { description }, {});
        const newId = res.data?.data?.id;
        toast.success({ message: 'Scope created' });
        navigate(`/scope/detail/${newId}`);
      } else {
        await api.employeeApi.apiVversionScopeIdPut(idNumber, '1', { id: idNumber, description }, {});
        toast.success({ message: 'Scope saved' });
        setDisabled(true);
      }
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedFunc || !idNumber) return;
    try {
      await api.employeeApi.apiVversionFunctionalityScopePost('1', {
        idScope: idNumber,
        idFunctionality: selectedFunc.id,
      }, {});
      setSelectedFunc(null);
      fetchAssignments();
    } catch (e) {
      handleAPIError(e, toast, {});
    }
  };

  const handleRemove = (assignmentId: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try {
            await api.employeeApi.apiVversionFunctionalityScopeIdDelete(assignmentId, '1', {});
            fetchAssignments();
          } catch (e) {
            handleAPIError(e, toast, {});
          } finally {
            closeDialog(dialogId);
          }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  const assignmentColumns: GridColDef[] = [
    { field: 'functionalityIdentifier', headerName: 'Functionality', flex: 1 },
    {
      field: 'remove', headerName: '', width: 80, sortable: false,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={ENTITY_TYPE.ANY}
          onDelete={() => handleRemove(params.row.id)}
          deleteScopes={[]}
        />
      )
    }
  ];

  if (loading) return <Spinner />;

  return (
    <Box className='my-3' sx={{ width: '100%', p: 2 }}>
      <Card sx={{ p: 3, mb: 3 }}>
        <Button startIcon={<KeyboardBackspaceIcon />} onClick={() => navigate('/scopes')} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
          {isNew ? 'New Scope' : `Scope: ${description}`}
        </Typography>

        {!isNew && disabled && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="outlined" onClick={() => setDisabled(false)}>Edit</Button>
          </Box>
        )}

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={disabled}
          fullWidth
          sx={{ mb: 2 }}
          helperText="Scope identifier string, e.g. clients.read"
        />

        {(!disabled || isNew) && (
          <Box display="flex" justifyContent="flex-end" gap={1}>
            {!isNew && (
              <Button variant="outlined" onClick={() => { setDisabled(true); fetchScope(); }}>
                Cancel
              </Button>
            )}
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </Box>
        )}
      </Card>

      {!isNew && (
        <Card sx={{ p: 3 }}>
          <Typography color="primary" variant="h4" sx={{ mb: 1 }}>Assigned Functionalities</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Functionalities this scope grants access to.
          </Typography>

          <Box display="flex" gap={1} mb={2}>
            <Autocomplete
              sx={{ flex: 1 }}
              open={funcSearchOpen}
              onOpen={() => setFuncSearchOpen(true)}
              onClose={() => setFuncSearchOpen(false)}
              options={funcOptions}
              loading={funcSearchLoading}
              value={selectedFunc}
              onChange={(_, val) => setSelectedFunc(val)}
              onInputChange={(_, val) => setFuncSearchTerm(val)}
              getOptionLabel={(o) => o.identifier || ''}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography variant="body2">{o.identifier}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.name}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search functionality"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {funcSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button variant="contained" onClick={handleAssign} disabled={!selectedFunc}>
              Assign
            </Button>
          </Box>

          {assignmentsLoading ? <Spinner /> : (
            <DataGrid
              rows={assignments}
              columns={assignmentColumns}
              disableRowSelectionOnClick
              hideFooter={assignments.length <= 10}
            />
          )}
        </Card>
      )}
    </Box>
  );
};

export default ScopeDetail;
